import { Related, ScreenLink } from "./parts";

export function AuditLog() {
  return (
    <>
      <p>
        Every change made in the admin app writes a row to the audit log, in the same step as the change itself: if the row
        cannot be written, the change fails and nothing happens. Rows can never be edited or deleted.{" "}
        <ScreenLink href="/audit">Audit log</ScreenLink> lists them, newest first.
      </p>

      <h2>A row holds</h2>
      <ul>
        <li>
          <strong>When</strong> and <strong>who</strong>. The actor is shown by email where the app knows it, otherwise by
          their stable id: a <code>saml-…</code> pseudonym for staff, since the trail itself never stores an email.
        </li>
        <li>
          <strong>Action</strong>, such as <code>period.close</code>, <code>adjustment.approve</code> or{" "}
          <code>installer.change.apply</code>.
        </li>
        <li>
          <strong>Target</strong>: the customer and the thing changed.
        </li>
        <li>
          <strong>Detail</strong>: before and after values, and the reason where one was given. Never prompt text, AWS
          credentials, secrets or email addresses.
        </li>
      </ul>

      <h2 id="what-you-see">What you see</h2>
      <table>
        <thead>
          <tr>
            <th>Role</th>
            <th>Actions shown</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Super admin</td>
            <td>Everything, including sign-ins, sign-outs and ended sessions</td>
          </tr>
          <tr>
            <td>Billing viewer or admin</td>
            <td>Rate plans, assignments, billing mode, trials, adjustments, months, AWS costs and CUR</td>
          </tr>
          <tr>
            <td>Installer</td>
            <td>Installer sessions and changes, and AWS account links</td>
          </tr>
        </tbody>
      </table>
      <p>Filters narrow what your role can see; they never widen it.</p>

      <h2>Filters and export</h2>
      <ul>
        <li>Filter by time, customer, actor, action and target.</li>
        <li>
          The CSV export downloads the filtered rows, up to 20,000. A bigger export is refused: narrow the time
          range.
        </li>
      </ul>
      <p>Useful actions to filter on:</p>
      <table>
        <thead>
          <tr>
            <th>Action</th>
            <th>Answers</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>period.close</code>
            </td>
            <td>Who closed a month, and whether it was forced</td>
          </tr>
          <tr>
            <td>
              <code>adjustment.approve</code>
            </td>
            <td>Who approved a credit or correction</td>
          </tr>
          <tr>
            <td>
              <code>expense.finalize</code>, <code>expense.restate</code>
            </td>
            <td>Who froze or restated a month's AWS cost</td>
          </tr>
          <tr>
            <td>
              <code>period.late_unbilled</code>
            </td>
            <td>Late usage that landed in a not-billed month and was never invoiced</td>
          </tr>
          <tr>
            <td>
              <code>installer.change.approve</code>, <code>installer.change.apply</code>
            </td>
            <td>Who approved an installer change, and when it was written</td>
          </tr>
          <tr>
            <td>
              <code>aws_account.link</code>, <code>aws_account.unlink</code>
            </td>
            <td>When a customer's AWS account was linked or unlinked</td>
          </tr>
        </tbody>
      </table>

      <h2 id="customer-activity">A customer's Activity</h2>
      <p>
        Each customer's <strong>Activity</strong> tab is that customer's own timeline, newest first. Its{" "}
        <strong>Log</strong> sub-tab merges the customer's audit rows, only the actions a customer may see, with the
        requests its Bedrock guardrail blocked, intervened on or failed closed. The Guardrails tab's former Triggered card is
        now these rows; the tab's badge counts them.
      </p>
      <ul>
        <li>
          Each row has a <strong>Kind</strong>: Settings, Quotas, People, Keys, Sign-in, Notifications, Guardrail or
          Assistant. Filter by kind, person and dates, and download the visible rows as CSV.
        </li>
        <li>
          A Guardrail row opens the filter that acted, the model and the tool, never the prompt's text. For a prompt
          reader it links to the stored prompt, and that read is audited.
        </li>
        <li>
          <strong>Prompt logs</strong>, the second sub-tab, shows only when the customer logs prompts and you may read
          prompts.
        </li>
        <li>
          The customer's admins see the same Log on their proxy's Admin → Activity, with every staff action shown as{" "}
          <strong>AllCode support</strong>: no name, email or reason. This page, with staff identities, is AllCode's alone.
        </li>
      </ul>

      <Related to={["signing-in-and-roles", "usage-adjustments", "installer"]} />
    </>
  );
}
